import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Chip, EmptyState, Skeleton } from '../ui'
import { ApiError } from '../../api/client'
import { isBusy, useMergeDuplicates, useMergePreview } from '../../api/hooks'
import type {
  MergeAccessDecision,
  MergeBlocker,
  MergeDoor,
  MergeDuplicatesResult,
  MergeOutcome,
  MergeOutcomeKey,
  MergePreview,
  MergePreviewPerson,
  MergeReason,
} from '../../api/types'
import { useAuthStore } from '../../stores/auth'
import { MergeVerificationCard } from './MergeVerificationCard'

export const MERGE_STEPS = ['Survivor', 'Compare', 'Access', 'Confirm', 'Result'] as const

const OUTCOME_LABELS: Record<MergeOutcomeKey, { title: string; hint: string }> = {
  union: { title: 'Everything anyone has', hint: 'The merged person keeps every access group any of them holds.' },
  survivor_only: { title: 'Only the survivor’s access', hint: 'The others’ access groups are dropped.' },
  per_group: { title: 'Choose per group', hint: 'Pick exactly which access groups the merged person keeps.' },
}

/** Plain-words explanation of how the decision is realised. */
export function modeExplanation(mode: MergeOutcome['mode']): string {
  if (mode === 'explicit') return 'Access is pinned and won’t follow future occupancy changes.'
  if (mode === 'derived') return 'Access keeps following occupancy — it ends when the occupancy ends.'
  return 'This decision can’t be carried out.'
}

function BlockerList({ blockers, names }: { blockers: MergeBlocker[]; names: Map<string, string> }) {
  return (
    <section className="flex flex-col gap-2" aria-label="Blockers">
      {blockers.map((b) => (
        <div
          key={`${b.code}-${b.system.unique_id}`}
          data-testid={`blocker-${b.code}`}
          className="rounded-sm border-2 border-ostrel-danger bg-ostrel-danger/15 p-3"
        >
          <p className="text-[11px] font-semibold uppercase tracking-widest text-ostrel-danger">
            Blocks the merge · {b.code.replace(/_/g, ' ')} · {b.system.name}
          </p>
          <p className="mt-1 text-sm text-ostrel-ink">{b.message}</p>
          {b.records.length > 0 && (
            <ul className="mt-1 flex flex-col gap-0.5 text-[11px] text-ostrel-ink">
              {b.records.map((r) => (
                <li key={`${r.person}-${r.record}`}>
                  {names.get(r.person) ?? r.person}: record <span className="tnum font-mono">{r.record}</span>
                  {r.managed ? ' (managed by Ostrel)' : ' (not managed)'}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-[11px] text-ostrel-ink-muted">
            This can’t be acknowledged — remove or unlink one vendor record first, then reload this page.
          </p>
        </div>
      ))}
    </section>
  )
}

function personNames(preview: MergePreview): Map<string, string> {
  return new Map(preview.persons.map((p) => [p.unique_id, p.display_name || p.unique_id]))
}

function reasonText(r: MergeReason): string {
  if (r.source === 'rule') return `rule · occupancy at ${r.location?.name ?? 'a location'}`
  if (r.source === 'group') return `directory group ${r.directory_group?.name ?? ''}`.trim()
  return 'direct'
}

function MicroLabel({ children }: { children: ReactNode }) {
  return <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">{children}</p>
}

/** A row flagged as not shared by every person. */
function DiffRow({ differs, children }: { differs: boolean; children: ReactNode }) {
  return (
    <li
      data-diff={differs ? 'true' : undefined}
      className={`flex flex-wrap items-center gap-1.5 rounded-sm px-1.5 py-1 text-xs ${
        differs ? 'border-l-2 border-ostrel-warn bg-ostrel-warn/10' : ''
      }`}
    >
      {children}
      {differs && (
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-ostrel-warn" title="Not the same for everyone">
          differs
        </span>
      )}
    </li>
  )
}

function DoorList({
  doors,
  highlight,
  empty = 'No doors.',
}: {
  doors: MergeDoor[]
  highlight?: Set<string>
  empty?: string
}) {
  if (doors.length === 0) return <p className="text-xs text-ostrel-ink-muted">{empty}</p>
  return (
    <ul className="flex flex-col gap-0.5">
      {doors.map((d) => (
        <DiffRow key={d.door} differs={highlight?.has(d.door) ?? false}>
          <span className="min-w-0">
            <span className="block text-ostrel-ink">{d.name}</span>
            {d.path.length > 0 && <span className="block text-[10px] text-ostrel-ink-muted">{d.path.join(' › ')}</span>}
          </span>
          <span className="tnum text-[10px] text-ostrel-ink-muted">{d.schedule?.name ?? 'always'}</span>
        </DiffRow>
      ))}
    </ul>
  )
}

function Stepper({ step }: { step: number }) {
  return (
    <ol className="flex flex-wrap gap-2" aria-label="Merge steps">
      {MERGE_STEPS.map((label, i) => (
        <li
          key={label}
          aria-current={i === step ? 'step' : undefined}
          className={`tnum rounded-sm border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest ${
            i === step
              ? 'border-ostrel-gold bg-ostrel-gold/10 text-ostrel-gold'
              : i < step
                ? 'border-ostrel-line bg-ostrel-sand text-ostrel-ink'
                : 'border-ostrel-line bg-ostrel-card text-ostrel-ink-muted'
          }`}
        >
          {i + 1}. {label}
        </li>
      ))}
    </ol>
  )
}

// --- Step 1: survivor --------------------------------------------------------

function SurvivorStep({
  preview,
  survivor,
  suggested,
  onChange,
}: {
  preview: MergePreview
  survivor: string
  suggested: string | undefined
  onChange: (uid: string) => void
}) {
  return (
    <Card title="Who to keep">
      <p className="mb-3 text-xs text-ostrel-ink-muted">
        The survivor keeps its identity; everyone else is merged into it and deleted. The suggestion is the person with
        the most history.
      </p>
      <ul className="flex flex-col gap-2">
        {preview.persons.map((p) => (
          <li key={p.unique_id}>
            <label
              className={`flex cursor-pointer flex-wrap items-center gap-2 rounded-sm border p-3 ${
                p.unique_id === survivor ? 'border-ostrel-gold bg-ostrel-gold/5' : 'border-ostrel-line'
              }`}
            >
              <input
                type="radio"
                name="survivor"
                value={p.unique_id}
                checked={p.unique_id === survivor}
                onChange={() => onChange(p.unique_id)}
                className="accent-ostrel-gold"
              />
              <span className="text-sm font-medium text-ostrel-ink">{p.display_name || 'Unnamed'}</span>
              <span className="tnum font-mono text-[11px] text-ostrel-ink-muted">{p.unique_id}</span>
              {p.unique_id === suggested && <Chip tone="gold" label="suggested" />}
              {p.is_placeholder && <Chip tone="muted" label="placeholder" />}
              <Chip tone={p.status === 'active' ? 'ok' : 'muted'} label={p.status} />
              <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
                {p.counts.credentials} credentials · {p.counts.occupancies} occupancies · {p.counts.events} events
              </span>
            </label>
          </li>
        ))}
      </ul>
    </Card>
  )
}

// --- Step 2: comparison -------------------------------------------------------

function PersonColumn({
  person,
  preview,
  sharedLocations,
  sharedSystems,
}: {
  person: MergePreviewPerson
  preview: MergePreview
  sharedLocations: Set<string>
  sharedSystems: Set<string>
}) {
  const diffGroups = new Set(preview.diff.groups.map((g) => g.unique_id))
  const diffDoors = new Set(preview.diff.doors.map((d) => d.door))
  const { explicit, derived, overridden } = person.access_groups
  return (
    <section
      data-testid={`compare-${person.unique_id}`}
      className={`flex min-w-0 flex-col gap-4 rounded-sm border p-3 ${
        person.is_survivor ? 'border-ostrel-gold' : 'border-ostrel-line'
      }`}
    >
      <header>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ostrel-ink">{person.display_name || 'Unnamed'}</span>
          {person.is_survivor ? <Chip tone="gold" label="survivor" /> : <Chip tone="danger" label="merged away" />}
        </div>
        <span className="tnum font-mono text-[10px] text-ostrel-ink-muted">{person.unique_id}</span>
        {!person.mailbox_access && <Chip className="ml-2" tone="muted" label="no mailbox access" />}
      </header>

      <div>
        <MicroLabel>Credentials</MicroLabel>
        {person.credentials.length === 0 ? (
          <p className="text-xs text-ostrel-ink-muted">None.</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {person.credentials.map((c) => (
              <DiffRow key={c.unique_id} differs={false}>
                <Chip tone="muted" label={c.kind} />
                <span className="tnum font-mono text-ostrel-ink">{c.code}</span>
                <Chip tone={c.status === 'active' ? 'ok' : 'muted'} label={c.status} />
                {c.collapses && <Chip tone="warn" label="duplicate — dropped" />}
              </DiffRow>
            ))}
          </ul>
        )}
      </div>

      <div>
        <MicroLabel>Occupancies</MicroLabel>
        {person.occupancies.length === 0 ? (
          <p className="text-xs text-ostrel-ink-muted">None.</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {person.occupancies.map((o) => (
              <DiffRow key={o.unique_id} differs={!sharedLocations.has(o.location.unique_id)}>
                <span className="text-ostrel-ink">{o.location.name}</span>
                <Chip tone={o.kind === 'resident' ? 'gold' : 'muted'} label={o.kind} />
                {!o.current && <Chip tone="muted" label="not current" />}
                {o.collapses && <Chip tone="muted" label="already on survivor" />}
              </DiffRow>
            ))}
          </ul>
        )}
      </div>

      <div>
        <MicroLabel>Vendor systems</MicroLabel>
        {person.systems.length === 0 ? (
          <p className="text-xs text-ostrel-ink-muted">Not linked to any vendor system.</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {person.systems.map((s) => (
              <DiffRow key={s.uid} differs={!sharedSystems.has(s.uid)}>
                <span className="text-ostrel-ink">{s.name}</span>
                <Chip tone="muted" label={s.provider} />
                {s.dry_run && <Chip tone="muted" label="dry run" />}
              </DiffRow>
            ))}
          </ul>
        )}
      </div>

      <div>
        <MicroLabel>Access groups</MicroLabel>
        {explicit.length === 0 && derived.length === 0 ? (
          <p className="text-xs text-ostrel-ink-muted">None.</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {explicit.map((g) => (
              <DiffRow key={g.unique_id} differs={diffGroups.has(g.unique_id)}>
                <span className="text-ostrel-ink">{g.name}</span>
                <Chip tone="gold" label="explicit" title="Assigned directly — does not follow occupancy" />
                {!g.effective && <Chip tone="muted" label="not effective" />}
              </DiffRow>
            ))}
            {derived.map((g) => (
              <DiffRow key={g.unique_id} differs={diffGroups.has(g.unique_id)}>
                <span className="text-ostrel-ink">{g.name}</span>
                <Chip tone={g.source === 'group' ? 'info' : 'muted'} label={g.source} />
                <span className="w-full text-[10px] text-ostrel-ink-muted">
                  {g.reasons.map(reasonText).join('; ')}
                </span>
              </DiffRow>
            ))}
          </ul>
        )}
        {overridden && (
          <p className="mt-1 text-[10px] text-ostrel-ink-muted">
            Explicit groups override anything occupancy would grant this person.
          </p>
        )}
      </div>

      <div>
        <MicroLabel>Doors</MicroLabel>
        <DoorList doors={person.doors} highlight={diffDoors} empty="Opens no doors." />
      </div>
    </section>
  )
}

function CompareStep({ preview }: { preview: MergePreview }) {
  // A location/system is "shared" when every person has it.
  const shared = (lists: string[][]) => {
    const [first = [], ...rest] = lists
    return new Set(first.filter((x) => rest.every((l) => l.includes(x))))
  }
  const sharedLocations = shared(preview.persons.map((p) => p.occupancies.map((o) => o.location.unique_id)))
  const sharedSystems = shared(preview.persons.map((p) => p.systems.map((s) => s.uid)))
  return (
    <Card title="Side by side">
      <p className="mb-3 text-xs text-ostrel-ink-muted">
        {preview.access_identical
          ? 'Everyone opens the same doors on the same schedules.'
          : 'Highlighted rows are not the same for everyone.'}
      </p>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(16rem,1fr))] gap-3">
        {preview.persons.map((p) => (
          <PersonColumn
            key={p.unique_id}
            person={p}
            preview={preview}
            sharedLocations={sharedLocations}
            sharedSystems={sharedSystems}
          />
        ))}
      </div>
    </Card>
  )
}

// --- Step 3: access decision --------------------------------------------------

function OutcomeDetail({ outcome, doorNames }: { outcome: MergeOutcome; doorNames: Map<string, string> }) {
  const gained = new Set(outcome.doors_gained)
  return (
    <div className="flex flex-col gap-3" data-testid="outcome-detail">
      <div className="flex flex-wrap items-center gap-2">
        <MicroLabel>Result</MicroLabel>
        <Chip tone={outcome.mode === 'explicit' ? 'warn' : outcome.mode === 'derived' ? 'ok' : 'danger'} label={outcome.mode} />
      </div>
      <p className="text-xs text-ostrel-ink">{modeExplanation(outcome.mode)}</p>
      {outcome.note && <p className="text-[11px] text-ostrel-ink-muted">{outcome.note}</p>}
      {outcome.groups.length > 0 && (
        <div>
          <MicroLabel>Access groups after the merge</MicroLabel>
          <ul className="flex flex-wrap gap-1.5">
            {outcome.groups.map((g) => (
              <li key={g.unique_id}>
                <Chip
                  tone={g.follows_occupancy ? 'muted' : 'gold'}
                  label={`${g.name} · ${g.follows_occupancy ? 'follows occupancy' : 'pinned'}`}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <MicroLabel>Final doors ({outcome.doors.length})</MicroLabel>
        <DoorList doors={outcome.doors} highlight={gained} empty="The merged person opens no doors." />
      </div>
      {outcome.mailbox_access !== null && (
        <p className="text-xs text-ostrel-ink">
          Mailbox access: <span className="font-medium">{outcome.mailbox_access ? 'yes' : 'no'}</span>
        </p>
      )}
      {outcome.schedule_changed.length > 0 && (
        <div>
          <MicroLabel>Schedule changes</MicroLabel>
          <ul className="flex flex-col gap-0.5 text-xs text-ostrel-warn">
            {outcome.schedule_changed.map((uid) => (
              <li key={uid}>{doorNames.get(uid) ?? uid} — opens on a different schedule than today</li>
            ))}
          </ul>
        </div>
      )}
      {outcome.doors_lost.length > 0 && (
        <div>
          <MicroLabel>Doors no longer opened</MicroLabel>
          <ul className="flex flex-col gap-0.5 text-xs text-ostrel-danger">
            {outcome.doors_lost.map((d) => (
              <li key={d}>{doorNames.get(d) ?? d}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function AccessStep({
  preview,
  choice,
  onChoice,
  selected,
  onToggleGroup,
  groupNames,
}: {
  preview: MergePreview
  choice: MergeOutcomeKey
  onChoice: (key: MergeOutcomeKey) => void
  selected: string[]
  onToggleGroup: (uid: string) => void
  groupNames: Map<string, string>
}) {
  const doorNames = new Map<string, string>()
  for (const p of preview.persons) for (const d of p.doors) doorNames.set(d.door, d.name)

  if (preview.access_identical) {
    const outcome = preview.outcomes[preview.recommended_outcome]
    return (
      <Card title="Access">
        <p className="mb-3 text-sm text-ostrel-ink">
          Access is identical — everyone opens the same doors on the same schedules. Nothing to decide; confirm to keep
          it.
        </p>
        <OutcomeDetail outcome={outcome} doorNames={doorNames} />
      </Card>
    )
  }

  const unionGroups = preview.outcomes.union.target_groups
  return (
    <Card title="Access">
      <p className="mb-3 text-xs text-ostrel-ink-muted">Their access differs. Decide what the merged person may open.</p>
      <div className="flex flex-col gap-2" role="radiogroup" aria-label="Access decision">
        {(Object.keys(OUTCOME_LABELS) as MergeOutcomeKey[]).map((key) => {
          const outcome = preview.outcomes[key]
          const disabled = !outcome.realizable
          return (
            <label
              key={key}
              className={`flex flex-col gap-1 rounded-sm border p-3 ${
                disabled
                  ? 'cursor-not-allowed border-ostrel-line opacity-60'
                  : key === choice
                    ? 'cursor-pointer border-ostrel-gold bg-ostrel-gold/5'
                    : 'cursor-pointer border-ostrel-line'
              }`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name="access"
                  value={key}
                  checked={key === choice}
                  disabled={disabled}
                  onChange={() => onChoice(key)}
                  className="accent-ostrel-gold"
                />
                <span className="text-sm font-medium text-ostrel-ink">{OUTCOME_LABELS[key].title}</span>
                {key === preview.recommended_outcome && <Chip tone="muted" label="recommended" />}
                {outcome.realizable && (
                  <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
                    {outcome.doors.length} doors · {outcome.mode}
                  </span>
                )}
              </span>
              <span className="ml-5 text-[11px] text-ostrel-ink-muted">{OUTCOME_LABELS[key].hint}</span>
              {disabled && (
                <span className="ml-5 text-[11px] text-ostrel-danger">Not possible: {outcome.note}</span>
              )}
            </label>
          )
        })}
      </div>

      {choice === 'per_group' && (
        <fieldset className="mt-3 rounded-sm border border-ostrel-line p-3">
          <legend className="px-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Keep these access groups
          </legend>
          <ul className="flex flex-col gap-1">
            {unionGroups.map((g) => (
              <li key={g}>
                <label className="flex items-center gap-2 text-xs text-ostrel-ink">
                  <input
                    type="checkbox"
                    checked={selected.includes(g)}
                    onChange={() => onToggleGroup(g)}
                    className="accent-ostrel-gold"
                  />
                  {groupNames.get(g) ?? g}
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      <div className="mt-4 border-t border-ostrel-line pt-4">
        {preview.outcomes[choice].realizable ? (
          <OutcomeDetail outcome={preview.outcomes[choice]} doorNames={doorNames} />
        ) : (
          <p className="text-xs text-ostrel-danger">{preview.outcomes[choice].note}</p>
        )}
      </div>
    </Card>
  )
}

// --- Step 4: warnings + confirm ---------------------------------------------

function ConfirmStep({
  preview,
  outcome,
  acked,
  onAck,
}: {
  preview: MergePreview
  outcome: MergeOutcome
  acked: Set<string>
  onAck: (code: string, on: boolean) => void
}) {
  const survivor = preview.persons.find((p) => p.is_survivor)
  const others = preview.persons.filter((p) => !p.is_survivor)
  const { blockers, warnings } = preview
  return (
    <div className="flex flex-col gap-4">
      {blockers.length > 0 && <BlockerList blockers={blockers} names={personNames(preview)} />}
      {warnings.length > 0 && (
        <section className="flex flex-col gap-2" aria-label="Warnings">
          {warnings.map((w) => (
            <div
              key={w.code}
              data-testid={`warning-${w.code}`}
              className="rounded-sm border-2 border-ostrel-danger bg-ostrel-danger/10 p-3"
            >
              <p className="text-[11px] font-semibold uppercase tracking-widest text-ostrel-danger">
                {w.code.replace(/_/g, ' ')}
              </p>
              <p className="mt-1 text-sm text-ostrel-ink">{w.message}</p>
              <label className="mt-2 flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 accent-ostrel-danger"
                  checked={acked.has(w.code)}
                  onChange={(e) => onAck(w.code, e.target.checked)}
                  aria-label={`Acknowledge ${w.code}`}
                />
                <span className="text-xs text-ostrel-ink">I have checked this and still want to merge.</span>
              </label>
            </div>
          ))}
        </section>
      )}

      <Card title="Summary">
        <div className="flex flex-col gap-3 text-xs text-ostrel-ink">
          <p>
            Keep <span className="font-medium">{survivor?.display_name || survivor?.unique_id}</span>; merge and delete{' '}
            {others.map((p) => p.display_name || p.unique_id).join(', ')}.
          </p>
          <p>
            Final access: {outcome.doors.length} door{outcome.doors.length === 1 ? '' : 's'}, {outcome.mode} —{' '}
            {modeExplanation(outcome.mode)}
          </p>
          {preview.systems_after.length > 0 && (
            <p>Vendor links after the merge: {preview.systems_after.map((s) => s.name).join(', ')}.</p>
          )}
          {preview.notes.length > 0 && (
            <div>
              <MicroLabel>Notes</MicroLabel>
              <ul className="flex list-disc flex-col gap-1 pl-4 text-[11px] text-ostrel-ink-muted">
                {preview.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[11px] font-semibold text-ostrel-danger">There is no undo.</p>
        </div>
      </Card>
    </div>
  )
}

// --- Step 5: result -----------------------------------------------------------

function ResultStep({ result, pollMs }: { result: MergeDuplicatesResult; pollMs: number }) {
  return (
    <div className="flex flex-col gap-4">
      <Card title="Merged">
        <div className="flex flex-col gap-2 text-xs text-ostrel-ink">
          <p>
            Merged into{' '}
            <Link to={`/directory/${result.survivor.unique_id}`} className="font-medium text-ostrel-gold hover:text-ostrel-accent">
              {result.survivor.display_name || result.survivor.unique_id}
            </Link>
            . {result.doors.length} door{result.doors.length === 1 ? '' : 's'}, {result.mode}: {modeExplanation(result.mode)}
          </p>
          {result.systems.length > 0 && (
            <p className="text-ostrel-ink-muted">Vendor sync started for {result.systems.length} system(s).</p>
          )}
        </div>
      </Card>
      <MergeVerificationCard survivor={result.survivor.unique_id} verification={result.verification} pollMs={pollMs} />
    </div>
  )
}

// --- The wizard ---------------------------------------------------------------

/** Guided merge of a set of duplicate people (operator). Every step reads the
 *  same server-side simulation (merge-preview), which is re-run whenever the
 *  survivor or the per-group selection changes. */
export function MergeDuplicatesWizard({ persons, pollMs = 3000 }: { persons: string[]; pollMs?: number }) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [survivor, setSurvivor] = useState<string | undefined>(undefined)
  const [suggested, setSuggested] = useState<string | undefined>(undefined)
  // null = follow the preview's recommended_outcome until the operator picks.
  const [choice, setChoice] = useState<MergeOutcomeKey | null>(null)
  const [selected, setSelected] = useState<string[] | null>(null)
  const [acked, setAcked] = useState<Set<string>>(new Set())
  const [result, setResult] = useState<MergeDuplicatesResult | null>(null)
  const [staleNotice, setStaleNotice] = useState(false)

  const perGroup = choice === 'per_group' && selected !== null ? selected : undefined
  const preview = useMergePreview(persons, survivor, perGroup, result === null)
  const merge = useMergeDuplicates()
  const data = preview.data

  // The first preview (no survivor sent) carries the server's suggestion.
  useEffect(() => {
    if (data && suggested === undefined) setSuggested(data.survivor)
  }, [data, suggested])

  const groupNames = useMemo(() => {
    const names = new Map<string, string>()
    if (!data) return names
    for (const p of data.persons) {
      for (const g of p.access_groups.explicit) names.set(g.unique_id, g.name)
      for (const g of p.access_groups.derived) names.set(g.unique_id, g.name)
    }
    for (const g of data.outcomes.union.groups) names.set(g.unique_id, g.name)
    return names
  }, [data])

  if (result) {
    return (
      <div className="flex flex-col gap-4">
        <Stepper step={4} />
        <ResultStep result={result} pollMs={pollMs} />
      </div>
    )
  }

  if (preview.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Stepper step={step} />
        <Skeleton className="h-48" />
      </div>
    )
  }
  if (preview.isError && isBusy(preview.error)) {
    return (
      <EmptyState
        title="The preview is busy"
        hint="Another merge or sync is working on these people. Try again in a moment."
        action={
          <Button variant="ghost" size="sm" onClick={() => void preview.refetch()}>
            Try again
          </Button>
        }
      />
    )
  }
  if (preview.isError || !data) {
    return (
      <EmptyState
        title="Could not prepare the merge"
        hint={preview.error?.message}
        action={
          <Link to="/directory/duplicates" className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent">
            ← Back to duplicates
          </Link>
        }
      />
    )
  }

  const currentSurvivor = survivor ?? data.survivor
  const effectiveChoice: MergeOutcomeKey = data.access_identical
    ? data.recommended_outcome
    : (choice ?? data.recommended_outcome)
  const outcome = data.outcomes[effectiveChoice]
  const stale = preview.isPlaceholderData || preview.isFetching
  const { blockers, warnings } = data
  const unacked = warnings.filter((w) => !acked.has(w.code))

  const chooseSurvivor = (uid: string) => {
    setSurvivor(uid)
    setAcked(new Set()) // the warnings are per survivor
  }
  const chooseOutcome = (key: MergeOutcomeKey) => {
    setChoice(key)
    if (key === 'per_group' && selected === null) setSelected(data.outcomes.union.target_groups)
  }
  const toggleGroup = (uid: string) => {
    const base = selected ?? data.outcomes.union.target_groups
    setSelected(base.includes(uid) ? base.filter((g) => g !== uid) : [...base, uid])
  }

  const decision: MergeAccessDecision =
    effectiveChoice === 'per_group' ? { groups: selected ?? data.outcomes.union.target_groups } : effectiveChoice

  const submit = () => {
    merge.mutate(
      {
        persons,
        survivor: currentSurvivor,
        access: decision,
        acknowledge_warnings: warnings.map((w) => w.code).filter((c) => acked.has(c)),
        // For per_group this preview was called with the same groups=.
        outcome_hash: outcome.outcome_hash,
      },
      {
        onSuccess: (res) => {
          setResult(res)
          void queryClient.invalidateQueries({
            queryKey: [scope],
            predicate: (q) => q.queryKey[1] !== 'merge-preview',
          })
        },
        onError: (err) => {
          if (!(err instanceof ApiError) || err.status !== 409) return
          // 409 vendor_record_conflict / unacknowledged_warnings / preview_stale,
          // or a plain {detail} divergence: reload the preview either way.
          void preview.refetch()
          if (err.code === 'preview_stale') {
            // Access changed since the preview: re-review the decision.
            merge.reset()
            setAcked(new Set())
            setStaleNotice(true)
            setStep(2)
          }
        },
      },
    )
  }

  const canNext = !stale && step < 3 && (step !== 2 || outcome.realizable)
  const canMerge =
    !stale && outcome.realizable && blockers.length === 0 && unacked.length === 0 && !merge.isPending

  return (
    <div className="flex flex-col gap-4">
      <Stepper step={step} />

      {staleNotice && (
        <div className="rounded-sm border border-ostrel-warn bg-ostrel-warn/10 p-3 text-xs text-ostrel-ink" role="alert">
          <span className="font-semibold text-ostrel-warn">Access changed since the preview.</span> The preview has been
          re-run — review the access decision again before merging.
          <button type="button" className="ml-2 text-ostrel-gold hover:text-ostrel-accent" onClick={() => setStaleNotice(false)}>
            Dismiss
          </button>
        </div>
      )}
      {blockers.length > 0 && step < 3 && (
        <p className="rounded-sm border-2 border-ostrel-danger bg-ostrel-danger/10 p-2 text-xs text-ostrel-danger" role="alert">
          This merge is blocked: {blockers.map((b) => b.message).join(' ')}
        </p>
      )}

      {step === 0 && (
        <SurvivorStep preview={data} survivor={currentSurvivor} suggested={suggested} onChange={chooseSurvivor} />
      )}
      {step === 1 && <CompareStep preview={data} />}
      {step === 2 && (
        <AccessStep
          preview={data}
          choice={effectiveChoice}
          onChoice={chooseOutcome}
          selected={selected ?? data.outcomes.union.target_groups}
          onToggleGroup={toggleGroup}
          groupNames={groupNames}
        />
      )}
      {step === 3 && (
        <ConfirmStep
          preview={data}
          outcome={outcome}
          acked={acked}
          onAck={(code, on) => {
            const next = new Set(acked)
            if (on) next.add(code)
            else next.delete(code)
            setAcked(next)
          }}
        />
      )}

      {merge.isError && <p className="text-xs text-ostrel-danger">{merge.error.message}</p>}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" disabled={step === 0} onClick={() => setStep(step - 1)}>
          ← Back
        </Button>
        <span className="flex items-center gap-2">
          {stale && <span className="text-[11px] text-ostrel-ink-muted">Updating preview…</span>}
          {step === 3 && blockers.length > 0 && (
            <span className="text-[11px] text-ostrel-danger">Blocked — see above</span>
          )}
          {step === 3 && blockers.length === 0 && unacked.length > 0 && (
            <span className="text-[11px] text-ostrel-danger">
              Acknowledge {unacked.length} warning{unacked.length === 1 ? '' : 's'} to merge
            </span>
          )}
          {step < 3 ? (
            <Button variant="navy" size="sm" disabled={!canNext} onClick={() => setStep(step + 1)}>
              Next →
            </Button>
          ) : (
            <Button variant="danger" size="sm" disabled={!canMerge} onClick={submit}>
              {merge.isPending ? 'Merging…' : `Merge ${persons.length} people`}
            </Button>
          )}
        </span>
      </div>
    </div>
  )
}
